import type { KnowledgeArticle } from "../types"

export const vanAiPocNaarProductie: KnowledgeArticle = {
  id: "insight:van-ai-poc-naar-productie",
  pageType: "knowledge-article",
  slug: { nl: "van-ai-poc-naar-productie" },
  title: {
    nl: "Wat is een Proof of Concept (PoC)? | Van AI-PoC naar productie",
    en: "Van AI Proof-of-Concept naar Productie | De stappen die bepalen of het lukt",
  },
  description: {
    nl: "Wat is een proof of concept en waarom halen zoveel AI-PoC's nooit productie? De stappen die het verschil maken tussen een demo die indruk maakt en een systeem dat draait.",
    en: "Why so many AI proof-of-concepts never reach production, and which steps separate an impressive demo from a system that runs.",
  },
  h1: {
    nl: "Wat is een proof of concept? Van AI-PoC naar productie",
    en: "Wat is een proof of concept? Van AI-PoC naar productie",
  },
  published: "2026-08-26",
  modified: "2026-10-29",
  author: "Camiel Schmeitz",
  sections: [
    {
      type: "paragraph",
      text: "Een werkende AI-demo is verleidelijk makkelijk gemaakt. Een systeem bouwen dat dagelijks, betrouwbaar en onder toezicht van gebruikers draait — dát is het echte werk. Het merendeel van de AI-projecten die vastlopen, strandt precies op die overgang.",
    },
    {
      type: "heading",
      text: "Wat is een proof of concept?",
    },
    {
      type: "paragraph",
      text: "Een proof of concept (PoC) is een klein, snel gebouwd prototype dat aantoont dat een idee technisch haalbaar is en waarde kan opleveren. Bij AI beantwoordt een PoC meestal twee vragen: kan het model dit probleem met jouw data oplossen, en is de kwaliteit goed genoeg om verder te gaan? Een PoC is dus een beslismoment, geen eindproduct.",
    },
    {
      type: "heading",
      text: "Waarom PoC's stranden",
    },
    {
      type: "list",
      items: [
        "De demo werkte met uitgezochte voorbeelden; de praktijk levert rommelige input.",
        "Er was geen evaluatiemethode, dus niemand kan aantonen of productiekwaliteit gehaald wordt.",
        "Integratie bleek zwaarder dan gedacht: rechten, formats, randgevallen in bestaande systemen.",
        "Er was geen eigenaar voor beheer: geen monitoring, geen bijsturing, geen aanspreekpunt.",
      ],
    },
    {
      type: "heading",
      text: "Wat een PoC wél moet bewijzen",
    },
    {
      type: "paragraph",
      text: "Een goed ontworpen proof-of-concept is geen showpiece maar een experiment. Het bewijst of ontkracht één kernaanname met een meetbare maatstaf: haalt de classificatie de vereiste nauwkeurigheid op realistische data? Is de antwoordkwaliteit consistent genoeg voor dit doel? Is de doorlooptijd acceptabel? Wie die aanname niet benoemt, bouwt een demo zonder leerwaarde.",
    },
    {
      type: "heading",
      text: "De brug naar productie",
    },
    {
      type: "list",
      items: [
        "Evaluatiesets: verzamel realistische testvragen en -gevallen inclusief randgevallen; meet elke wijziging daartegen.",
        "Hardening: foutafhandeling, invoervalidatie, limieten en logging vanaf het begin meedesignen.",
        "Menselijke controle waar risico's zitten: goedkeurstappen zijn geen zwakte maar ontwerpkeuze.",
        "Monitoring: kwaliteit, kosten en gebruik continu zichtbaar — modellen veranderen onder je handen.",
        "Documentatie en overdracht: een systeem dat niemand anders kan begrijpen is geen productiesysteem.",
      ],
    },
    {
      type: "heading",
      text: "Uit onze praktijk",
    },
    {
      type: "paragraph",
      text: "Bij De Limburger verkenden we eerst bewust de grenzen van de publicatieplatforms voordat we de workflow bouwden — dat soort randvoorwaarden ontdek je liever in een verkenning dan in productie. Bij het tradingplatform voor een institutionele klant draaide het om continue, autonome verwerking van realtime marktinformatie; daar is productierigheid geen optie maar de kernvereiste.",
    },
    {
      type: "callout",
      title: "Onze insteek",
      text: "Snelle iteratie betekent geen concessies — het betekent sneller perfectioneren. Kort itereren op een goede evaluatieset brengt je eerder bij productiekwaliteit dan lang polijsten aan een demo.",
    },
  ],
  relatedServiceIds: ["ai-development"],
  relatedCaseIds: ["de-limburger", "quantitative-trading"],
}
